import { useEffect, useState } from 'react';
import axios from 'axios';
import { WORKING_CALENDAR_ENABLED } from '../config/featureFlags';
import { ResolvedCalendar } from '../modules/calendarEngine';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

/**
 * Fetches and resolves a project's working calendar (week + exceptions + expanded
 * holiday dates) for date-math purposes (cascade, critical path, PV, Gantt shading).
 * Returns undefined whenever the feature flag is off, the project has no calendar_id,
 * or the range isn't known yet — callers pass `undefined` straight through to the
 * calendar-aware functions, which fall back to legacy Standard-calendar behavior.
 */
export function useResolvedCalendar(
  calendarId: string | null | undefined,
  from: string | null | undefined,
  to: string | null | undefined,
  token: string,
  // Bump this (e.g. after any Working Calendar modal save) to force a refetch even when
  // calendarId/from/to didn't change — editing a calendar's OWN week/exceptions/holidays
  // doesn't touch any of those three, so without this the hook keeps serving stale
  // holiday/exception data (wrong Gantt durations, wrong weekend/holiday shading) until
  // something unrelated happens to shift the date range.
  refreshKey?: number | string,
): ResolvedCalendar | undefined {
  const [calendar, setCalendar] = useState<ResolvedCalendar | undefined>(undefined);

  useEffect(() => {
    if (!WORKING_CALENDAR_ENABLED || !calendarId || !from || !to) {
      setCalendar(undefined);
      return;
    }
    let cancelled = false;
    axios.get(`${API}/calendars/${calendarId}/resolved`, {
      params: { from, to },
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      if (cancelled) return;
      const data = res.data;
      setCalendar({
        week: data.week,
        exceptions: data.exceptions || [],
        holidayDates: new Set((data.holidayDates || []).map((h: any) => h.date)),
      });
    }).catch(err => {
      console.error('Failed to resolve project calendar, falling back to Standard:', err?.message || err);
      if (!cancelled) setCalendar(undefined);
    });
    return () => { cancelled = true; };
  }, [calendarId, from, to, token, refreshKey]);

  return calendar;
}
